import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  Injector,
  input,
  InputSignal,
  Signal,
  WritableSignal,
  computed,
  effect,
  signal,
  EffectCleanupRegisterFn,
} from '@angular/core';
import { DsButtonVariants, DsCheckboxesFeedbackOption, IconScale, Icons } from '@levelaccess/design-system';
import { Subject, Subscription } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, pairwise } from 'rxjs/operators';
import { IChatMessageStatus } from '../../../../../shared/interfaces/ai-chat.interface';
import { TranslateService } from '../../../translate/translate.service';
import { UserService } from '../../../services/user.service';
import { $user } from '../../../../../shared/constants/user';
import { AiChatSampleQuestionsComponent } from '../../ai-chat-sample-questions/ai-chat-sample-questions.component';
import { AiChatStatus, IChatContentItem, IChatMessage } from '../ai-chat-base/ai-chat.interface';
import { serializeComponentChunk } from '../generative-components/ai-chat-copy-serializer.utility';

import { ResponseRating } from '../response-rating.enum';
import { AiChatMessageToolCall } from './ai-chat.messages.types';

@Component({
  selector: 'app-ai-chat-messages',
  templateUrl: './ai-chat-messages.component.html',
  styleUrls: ['./ai-chat-messages.component.scss'],
  standalone: false,
})
export class AiChatMessagesComponent implements OnInit, OnChanges, OnDestroy {
  messages: InputSignal<IChatMessage[]> = input([]);
  @Input() status: AiChatStatus = AiChatStatus.IDLE;
  @Input() refreshSampleQuestionsRequest$: Subject<void>;
  @Input() focusErrorRequest$: Subject<string | undefined>;
  @Input() showUxImprovements: boolean = false;

  @ViewChildren('aiResponseRef') private aiResponseRefs: QueryList<ElementRef<HTMLElement>>;
  protected ratingTitle: string;
  protected ratingOptions: DsCheckboxesFeedbackOption<number>[];
  protected userName: string;
  /**
   * Emits true after response streaming has paused for 200ms
   */
  protected hasResponseStreamPaused: WritableSignal<boolean> = signal(true);
  private lastMessageResponse: Signal<string> = computed(() => this.messages().at(-1)?.response);
  /**
   * Emit latest tool call w/o duplicates when streaming naturally or arbitrarily stops.
   */
  protected activeToolCall: Signal<AiChatMessageToolCall | null> = toSignal(
    toObservable(
      // grab latest
      computed(() => {
        const lastMessage = this.messages().at(-1);
        const lastEvent = lastMessage?.events?.at(-1);
        if (!lastEvent || !lastEvent.annotation) {
          return null;
        }
        return {
          id: lastEvent.id,
          annotation: lastEvent.annotation,
          icon: this.activeToolCallIcons[Math.floor(Math.random() * this.activeToolCallIcons.length)],
        };
      }),
    ).pipe(
      // dedupe
      pairwise(),
      map(([prev, curr]: [AiChatMessageToolCall | null, AiChatMessageToolCall | null]) => {
        if (!curr) {
          return null;
        }
        if (prev?.id === curr.id) {
          return null;
        }
        return curr;
      }),
    ),
    { initialValue: null },
  );
  private activeToolCallIcons: Icons[] = [Icons.Wrench, Icons.ScrewdriverWrench, Icons.Eye, Icons.MagnifyingGlass];

  @ViewChild(AiChatSampleQuestionsComponent) private sampleQuestionsComponent: AiChatSampleQuestionsComponent;

  @Output() sampleQuestionClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() retry: EventEmitter<{ message: string; index: number }> = new EventEmitter<{ message: string; index: number }>();
  @Output() ratingChange: EventEmitter<{ message: IChatMessage; rating: number }> = new EventEmitter<{
    message: IChatMessage;
    rating: number;
  }>();
  @Output() refreshSampleQuestionsRequest: EventEmitter<void> = new EventEmitter<void>();

  protected readonly Icons: typeof Icons = Icons;
  protected readonly IconScale: typeof IconScale = IconScale;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly IChatMessageStatus: typeof IChatMessageStatus = IChatMessageStatus;
  protected readonly AiChatStatus: typeof AiChatStatus = AiChatStatus;

  /**
   * Zone 1: first sentence, set once early during streaming.
   * Zone 2: the entire remaining response, set once when the message is done.
   * Both zones are set exactly once per message — no incremental updates.
   */
  protected firstAnnouncedChunk: WritableSignal<string> = signal('');
  protected remainingAnnouncedChunk: WritableSignal<string> = signal('');

  private _subscription: Subscription = new Subscription();
  private _announcingSessionActive: boolean = false;
  private _wasLoadingOrStreaming: boolean = false;
  private _firstChunkAnnounced: boolean = false;
  private _lastAnnouncedMsgId: string | null = null;
  private _lastAnnouncedOffset: number = 0;
  private _srZone1AccumulationThreshold: number = 200;
  private _zone2Timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private injector: Injector,
    private translateService: TranslateService,
  ) {
    effect((onCleanup: EffectCleanupRegisterFn) => {
      this.lastMessageResponse();
      this.hasResponseStreamPaused.set(false);
      const timer = setTimeout(() => this.hasResponseStreamPaused.set(true), 200);
      onCleanup(() => clearTimeout(timer));
    });

    effect(() => {
      const lastMsg = this.messages().at(-1);

      if (!lastMsg?.response || !this._announcingSessionActive) return;

      if (lastMsg._id !== this._lastAnnouncedMsgId) {
        this._resetAnnouncementState(lastMsg._id);
      }

      const remaining = lastMsg.response.slice(this._lastAnnouncedOffset);

      if (lastMsg.status === IChatMessageStatus.done) {
        this._announceOnDone(remaining, lastMsg.response.length);
        return;
      }

      if (!remaining) return;

      if (lastMsg.status === IChatMessageStatus.processing && !this._firstChunkAnnounced) {
        this._announceFirstChunkIfReady(remaining);
      } else if (lastMsg.status === IChatMessageStatus.error) {
        this._announcingSessionActive = false;
      }
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    const isLoadingOrStreaming =
      this.status === AiChatStatus.STREAMING_IN_PROGRESS || this.status === AiChatStatus.WAITING_FOR_STATIC_RESPONSE;

    if (isLoadingOrStreaming) {
      this._announcingSessionActive = true;
    } else if (this._wasLoadingOrStreaming) {
      queueMicrotask(() => {
        this._announcingSessionActive = false;
      });
    }
    this._wasLoadingOrStreaming = isLoadingOrStreaming;
  }

  ngOnInit(): void {
    const userService = this.injector.get(UserService);

    this.ratingTitle = this.translateService.instant('ai-chat-message-rating-title');
    this.userName = userService.retrieveAuthenticatedUserProfile()[$user.displayName];
    this.ratingOptions = [
      {
        label: this.translateService.instant('good'),
        id: 'rating-good',
        value: ResponseRating.good,
        icon: Icons.ThumbsUp,
      },
      {
        label: this.translateService.instant('bad'),
        id: 'rating-bad',
        value: ResponseRating.bad,
        icon: Icons.ThumbsDown,
      },
    ];

    if (this.refreshSampleQuestionsRequest$) {
      this._subscription.add(
        this.refreshSampleQuestionsRequest$.subscribe(() => {
          this.refreshSampleQuestions();
        }),
      );
    }

    if (this.focusErrorRequest$) {
      this._subscription.add(
        this.focusErrorRequest$.subscribe((messageId: string | undefined) => {
          setTimeout(() => {
            const target = messageId
              ? this.aiResponseRefs.find((ref: ElementRef<HTMLElement>) => ref.nativeElement.id === `ai-response-${messageId}`)
              : this.aiResponseRefs.find((ref: ElementRef<HTMLElement>) =>
                  ref.nativeElement.classList.contains(IChatMessageStatus.error),
                );
            target?.nativeElement.focus();
          });
        }),
      );
    }
  }

  public onSampleQuestionClick(question: string): void {
    this.sampleQuestionClick.emit(question);
  }

  public onRetry(message: string, index: number): void {
    this.retry.emit({ message, index });
  }

  public onRatingChange(message: IChatMessage, rating: number): void {
    this.ratingChange.emit({ message, rating });
  }

  public getCopyText(message: IChatMessage): string {
    if (!message.contentBlocks?.length) {
      return this.stripMarkdownLinks(message.response);
    }

    return message.contentBlocks
      .map((item: IChatContentItem) =>
        item.type === 'text' ? this.stripMarkdownLinks(item.text) : serializeComponentChunk(item),
      )
      .filter(Boolean)
      .join('\n\n');
  }

  // Copied text is pasted outside the platform, where links into the app (or relative hrefs) aren't meaningful —
  // keep the visible label, drop the URL.
  private stripMarkdownLinks(text: string): string {
    return text?.replace(/\[(.*?)\]\((.*?)\)/g, '$1') ?? text;
  }

  private normalizeMessageForAriaLabel(message: string): string {
    if (!message) {
      return '';
    }

    // Complete table: header row + separator + data rows
    const completeTableRegex = /^(\|.+)\n\|[-: |]+\|((?:\n\|.+)*)/gm;

    return message
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
      .replace(completeTableRegex, (_match: string, headerRow: string, bodyRows: string) => {
        const headers = headerRow
          .split('|')
          .map((h: string) => h.trim())
          .filter(Boolean);
        const rows = bodyRows.trim().split('\n').filter(Boolean);
        if (!rows.length) return '';
        return rows
          .map((row: string) => {
            const cells = row
              .split('|')
              .map((c: string) => c.trim())
              .filter(Boolean);
            return headers.map((h: string, i: number) => `${h}: ${cells[i] ?? ''}`).join(', ') + '.';
          })
          .join(' ');
      })
      .replace(/^\|.+$/gm, (row: string) =>
        row
          .split('|')
          .map((c: string) => c.trim())
          .filter(Boolean)
          .join(', '),
      )
      .replace(/[#>*_`~-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private extractFirstZoneChunk(text: string): string {
    const boundaryRegex = /[.!?]+\s+|\n{2,}/;
    let accumulated = '';
    let remaining = text;

    while (accumulated.length <= this._srZone1AccumulationThreshold) {
      const match = boundaryRegex.exec(remaining);
      if (!match) break;
      accumulated += remaining.slice(0, match.index + match[0].length);
      remaining = remaining.slice(match.index + match[0].length);
    }

    return accumulated;
  }

  public refreshSampleQuestions(): void {
    setTimeout(() => {
      if (this.sampleQuestionsComponent) {
        this.sampleQuestionsComponent.refresh();
      }
    });
  }

  private _resetAnnouncementState(msgId: string): void {
    this.firstAnnouncedChunk.set('');
    this.remainingAnnouncedChunk.set('');
    this._firstChunkAnnounced = false;
    this._lastAnnouncedMsgId = msgId;
    this._lastAnnouncedOffset = 0;
  }

  private _announceOnDone(remaining: string, fullLength: number): void {
    const responseComplete = this.translateService.instant('ai_response_complete');

    if (this._firstChunkAnnounced) {
      const normalized = this.normalizeMessageForAriaLabel(remaining);
      this.remainingAnnouncedChunk.set(normalized ? `${normalized} ${responseComplete}` : responseComplete);
    } else {
      const rawFirst = this.extractFirstZoneChunk(remaining);
      if (rawFirst) {
        this.firstAnnouncedChunk.set(this.normalizeMessageForAriaLabel(rawFirst));
        this._scheduleZone2(this.normalizeMessageForAriaLabel(remaining.slice(rawFirst.length)), responseComplete);
      } else {
        this.firstAnnouncedChunk.set(`${this.normalizeMessageForAriaLabel(remaining)} ${responseComplete}`.trim());
      }
    }

    this._lastAnnouncedOffset = fullLength;
    this._announcingSessionActive = false;
  }

  private _scheduleZone2(rest: string, responseComplete: string): void {
    const capturedMsgId = this._lastAnnouncedMsgId;
    if (this._zone2Timer !== null) clearTimeout(this._zone2Timer);
    // Defer to the next macrotask so Angular commits zone 1 to the DOM first.
    // Screen readers announce zone 1 before zone 2 because the mutations arrive in
    // separate tasks. 0 ms is the minimum needed — no arbitrary delay.
    this._zone2Timer = setTimeout(() => {
      this._zone2Timer = null;
      if (this._lastAnnouncedMsgId !== capturedMsgId) return;
      this.remainingAnnouncedChunk.set(rest ? `${rest} ${responseComplete}` : responseComplete);
    }, 0);
  }

  private _announceFirstChunkIfReady(remaining: string): void {
    // Wait until enough sentences accumulate before setting Zone 1,
    // so a single short sentence ending on a newline doesn't trigger Zone 1 prematurely.
    const rawFirst = this.extractFirstZoneChunk(remaining);
    if (rawFirst.length > this._srZone1AccumulationThreshold) {
      this._lastAnnouncedOffset += rawFirst.length;
      const normalized = this.normalizeMessageForAriaLabel(rawFirst);
      if (normalized) {
        this.firstAnnouncedChunk.set(normalized);
        this._firstChunkAnnounced = true;
      }
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    if (this._zone2Timer !== null) {
      clearTimeout(this._zone2Timer);
      this._zone2Timer = null;
    }
  }
}
